/**
 * Singleton PosClient - wired with the env-time API base URL plus a
 * TokenStore. Storage strategy is split (see ``./auth`` for the full
 * rationale):
 * - access + id tokens → localStorage (shared across tabs so
 *   row-click → new-tab inherits the session)
 * - active membership id → sessionStorage (per-tab; cross-tab
 *   sharing would race against per-tab React Query caches)
 * - refresh token → memory only (no consumer; persisting it on a
 *   shared POS terminal is a real exposure)
 */

import { PosClient } from "@tilt/pos-sdk";

import { clearSession, makeTokenStore, readActiveMembershipId } from "./auth";

let _client: PosClient | null = null;

// Module-level flag set by AppShell via setApiLocked(). While true, a
// 401 means the session expired during an idle lock period; the LockScreen
// already handles that case on mount (redirect to /login when token gone),
// so onUnauthorized should NOT fire a competing redirect that races with
// the lock screen render.
let _isScreenLocked = false;
export function setApiLocked(locked: boolean): void {
  _isScreenLocked = locked;
}

function onUnauthorized(): void {
  // Suppress while the lock screen is visible - it handles its own
  // token-expiry redirect to avoid a race between onUnauthorized's
  // window.location.assign and the lock screen's useEffect navigate.
  if (_isScreenLocked) {
    console.debug("[api] onUnauthorized suppressed — screen is locked");
    return;
  }
  // The 401 is authoritative - the access token in localStorage is
  // either expired or signed by a now-replaced pool. Wipe it BEFORE
  // bouncing to /login, otherwise LoginPage's ``if (readAccessToken())
  // return <Navigate to={from} />`` early return reads the stale token
  // and bounces back to ``/``, which 401s again, which calls us again
  // - redirect loop.
  console.debug("[api] onUnauthorized — clearing session and redirecting to /login");
  if (typeof window !== "undefined") {
    clearSession();
    window.location.assign("/login");
  }
}

export function getPosClient(): PosClient {
  if (_client) return _client;
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error(
      "VITE_API_BASE_URL is not set - cannot build PosClient. " +
        "Set it at build time so the bundle knows where tilt-pos-api lives.",
    );
  }
  _client = new PosClient({
    baseUrl,
    tokens: makeTokenStore(onUnauthorized),
    getActiveMembershipId: () => readActiveMembershipId(),
  });
  return _client;
}

// V0080 - public receipt envelope returned by GET /pos/v1/public/receipts/by-slug/{slug}.
// Mirrors the authed receipt shape (order + payments) plus the slug's
// target_kind so the page can render the matching template.
export interface PublicReceiptEnvelope {
  readonly kind: "receipt_order" | "receipt_refund" | "receipt_void" | "receipt_payment";
  readonly target_id: string;
  readonly featured_payment_id?: string | null;
  readonly order: Record<string, unknown>;
  readonly payments: ReadonlyArray<Record<string, unknown>>;
  readonly vendor_receipt_url?: string | null;
}

/**
 * Fetch the customer-facing receipt envelope for a short-link slug.
 *
 * No auth header - the slug is the access check on the server. Returns
 * ``null`` on 404 (slug missing / tampered) so the page can render a
 * friendly empty state. Other errors throw so the page can surface a
 * reload affordance.
 */
export async function fetchPublicReceiptBySlug(
  slug: string,
): Promise<PublicReceiptEnvelope | null> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL is not set");
  }
  const url = `${String(baseUrl).replace(/\/$/, "")}/pos/v1/public/receipts/by-slug/${encodeURIComponent(slug)}`;
  const resp = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (resp.status === 404) return null;
  if (!resp.ok) {
    throw new Error(`receipt fetch failed: ${resp.status}`);
  }
  return (await resp.json()) as PublicReceiptEnvelope;
}

/**
 * V0080 - cashier-driven manual receipt send.
 *
 * Wraps ``POST /pos/v1/orders/{id}/receipts/send`` so callers don't
 * have to construct the endpoint path. The backend resolves the
 * matching short-link, builds the SMS body, and dispatches via the
 * V0076 notifications binding cascade.
 */
export interface SendReceiptResult {
  readonly sent: boolean;
  readonly skipped_reason: string;
  readonly vendor_message_id: string;
  readonly vendor_status: string;
  readonly provider_slug: string;
  readonly kind: "charge" | "refund" | "void";
  readonly error?: string | undefined;
}

export async function sendPaymentReceipt(
  orderId: string,
  body: {
    kind: "charge" | "refund" | "void";
    target_id?: string;
    payment_id?: string;
    phone?: string;
  },
): Promise<SendReceiptResult> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL is not set");
  }
  const tokens = makeTokenStore(onUnauthorized);
  const accessToken = tokens.getAccessToken();
  const membershipId = readActiveMembershipId();
  const url = `${String(baseUrl).replace(/\/$/, "")}/pos/v1/orders/${encodeURIComponent(orderId)}/receipts/send`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(membershipId ? { "X-Active-Membership-Id": membershipId } : {}),
    },
    body: JSON.stringify(body),
  });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  const payload = (await resp.json().catch(() => ({}))) as Partial<SendReceiptResult>;
  if (!resp.ok && resp.status !== 502) {
    throw new Error(payload.error ?? `send-receipt failed: ${resp.status}`);
  }
  return {
    sent: Boolean(payload.sent),
    skipped_reason: payload.skipped_reason ?? "",
    vendor_message_id: payload.vendor_message_id ?? "",
    vendor_status: payload.vendor_status ?? "",
    provider_slug: payload.provider_slug ?? "",
    kind: payload.kind ?? body.kind,
    error: payload.error,
  };
}

// -------------------------------------------------------------------------- //
// Payment link SMS send
// -------------------------------------------------------------------------- //

export interface SendPaymentLinkResult {
  readonly sent: boolean;
  readonly skipped_reason: string;
  readonly vendor_message_id: string;
  readonly vendor_status: string;
  readonly provider_slug: string;
  readonly error?: string | undefined;
}

export async function sendPaymentLink(
  orderId: string,
  phone: string,
): Promise<SendPaymentLinkResult> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) throw new Error("VITE_API_BASE_URL is not set");
  const tokens = makeTokenStore(onUnauthorized);
  const accessToken = tokens.getAccessToken();
  const membershipId = readActiveMembershipId();
  const url = `${String(baseUrl).replace(/\/$/, "")}/pos/v1/orders/${encodeURIComponent(orderId)}/payment-link/send`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(membershipId ? { "X-Active-Membership-Id": membershipId } : {}),
    },
    body: JSON.stringify({ phone }),
  });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  const payload = (await resp.json().catch(() => ({}))) as Partial<SendPaymentLinkResult>;
  if (!resp.ok && resp.status !== 502) {
    throw new Error(payload.error ?? `send-payment-link failed: ${resp.status}`);
  }
  return {
    sent: Boolean(payload.sent),
    skipped_reason: payload.skipped_reason ?? "",
    vendor_message_id: payload.vendor_message_id ?? "",
    vendor_status: payload.vendor_status ?? "",
    provider_slug: payload.provider_slug ?? "",
    error: payload.error,
  };
}

// -------------------------------------------------------------------------- //
// AI Recommendations - V0084
// -------------------------------------------------------------------------- //

export type AiSeverity = "info" | "opportunity" | "attention";

export interface AiRecommendation {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly severity: AiSeverity;
  readonly metric?: { readonly label: string; readonly value: string };
  readonly cta_route?: string;
}

export interface AiLocationTile {
  readonly id: string;
  readonly body: string;
  readonly severity: AiSeverity;
  readonly metric_label?: string;
  readonly metric_value?: string;
}

export interface DashboardRecommendationsResult {
  readonly recommendations: AiRecommendation[];
  readonly rendered_at: string;
  readonly expires_at: string;
  readonly source: "cache" | "fresh" | "stale" | "baseline_skip";
  readonly cost_usd_month_to_date: number;
  /** V0087 - opaque ID of the cached recommendation row. Present when the
   *  response has a persisted output (all non-error paths). Used to submit
   *  thumbs-up/down feedback against the exact output the cashier saw. */
  readonly recommendation_id?: string;
}

export interface LocationRecommendationsResult {
  readonly tiles: AiLocationTile[];
  readonly rendered_at: string;
  readonly expires_at: string;
  readonly source: "cache" | "fresh" | "stale" | "baseline_skip";
  readonly cost_usd_month_to_date: number;
  readonly recommendation_id?: string;
}

export type AiFeedbackRating = 1 | -1;
export type AiFeedbackReasonCode = "inaccurate" | "irrelevant" | "too_generic";

export interface AiFeedbackRequest {
  readonly recommendation_id: string;
  readonly rating: AiFeedbackRating;
  readonly reason_code?: AiFeedbackReasonCode;
  readonly free_text?: string;
}

async function _authHeaders(): Promise<Record<string, string>> {
  const tokens = makeTokenStore(onUnauthorized);
  const accessToken = tokens.getAccessToken();
  const membershipId = readActiveMembershipId();
  return {
    Accept: "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(membershipId ? { "X-Active-Membership-Id": membershipId } : {}),
  };
}

function _apiBase(): string {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) throw new Error("VITE_API_BASE_URL is not set");
  return String(base).replace(/\/$/, "");
}

export async function fetchDashboardRecommendations(
  period: "daily" | "weekly" | "monthly" = "daily",
  locationId?: string,
): Promise<DashboardRecommendationsResult | null> {
  const params = new URLSearchParams({ period });
  if (locationId) params.set("location_id", locationId);
  const url = `${_apiBase()}/pos/v1/recommendations/dashboard?${params.toString()}`;
  const resp = await fetch(url, { headers: await _authHeaders() });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  // 404 = feature not entitled; 403 = no pos.ai.recommendations.view permission.
  // Both hide the card silently - the user simply doesn't see the widget.
  if (resp.status === 404 || resp.status === 403) return null;
  if (!resp.ok) throw new Error(`recommendations/dashboard failed: ${resp.status}`);
  return (await resp.json()) as DashboardRecommendationsResult;
}

export async function fetchLocationRecommendations(
  locationId: string,
): Promise<LocationRecommendationsResult | null> {
  const url = `${_apiBase()}/pos/v1/recommendations/location/${encodeURIComponent(locationId)}`;
  const resp = await fetch(url, { headers: await _authHeaders() });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  // 404 = feature not entitled; 403 = no pos.ai_recommendations.view permission.
  if (resp.status === 404 || resp.status === 403) return null;
  if (!resp.ok) throw new Error(`recommendations/location failed: ${resp.status}`);
  return (await resp.json()) as LocationRecommendationsResult;
}

export async function regenerateDashboardRecommendations(
  period: "daily" | "weekly" | "monthly" = "daily",
  locationId?: string,
): Promise<DashboardRecommendationsResult> {
  const params = new URLSearchParams({ period });
  if (locationId) params.set("location_id", locationId);
  const url = `${_apiBase()}/pos/v1/recommendations/dashboard/regenerate?${params.toString()}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: await _authHeaders(),
  });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  if (!resp.ok) throw new Error(`recommendations/dashboard/regenerate failed: ${resp.status}`);
  return (await resp.json()) as DashboardRecommendationsResult;
}

export async function regenerateLocationRecommendations(
  locationId: string,
): Promise<LocationRecommendationsResult> {
  const url = `${_apiBase()}/pos/v1/recommendations/location/${encodeURIComponent(locationId)}/regenerate`;
  const resp = await fetch(url, {
    method: "POST",
    headers: await _authHeaders(),
  });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  if (!resp.ok) throw new Error(`recommendations/location/regenerate failed: ${resp.status}`);
  return (await resp.json()) as LocationRecommendationsResult;
}

/** POST /pos/v1/ai/feedback - submit thumbs-up/down for a recommendation.
 *  Idempotent: calling again with a different rating flips the vote. */
export async function submitAiFeedback(body: AiFeedbackRequest): Promise<void> {
  const url = `${_apiBase()}/pos/v1/ai/feedback`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { ...(await _authHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (resp.status === 401 && typeof window !== "undefined") {
    onUnauthorized();
    throw new Error("unauthorized");
  }
  if (!resp.ok) throw new Error(`ai/feedback failed: ${resp.status}`);
}
